<template>
  <div
    :class="{
      'processing': item.cartStatus === 0,
      'sku-item--disabled': item.disabled
    }"
    class="sku-item"
  >
    <div
      v-if="item.cartStatus === 0 && !allProcessing"
      class="processing"
    >
      Processing
    </div>
    <div class="sku-item__content">
      <div class="sku-item-l">
        <div
          @click="handleCheckClick"
          class="check-icon"
        >
          <CartsSelect
            :selected="item.checked"
            :disabled="item.disabled"
          />
        </div>
        <Image
          class="image"
          :src="item.skuImageUrl"
          alt=""
        />
        <Tooltip
          :content="item.skuSpec"
        >
          <div class="skuSpec">
            {{ item.skuSpec }}
          </div>
        </Tooltip>
        <div class="number-input-wrapper">
          <!-- 当 skuType 为 2（服务）时，该 sku 数量只能为 1 -->
          <NumberInput
            v-model="item.quantity"
            :disabled-minus="item.disabled || item.quantity <= item.bundleQuantity"
            :disabled-plus="item.disabled"
            :min="1"
            :max="item.skuType === 2 ? 1 : undefined"
            :step="item.bundleQuantity"
          />
        </div>
        <div
          @click="handleDeleteClick"
          class="delete-icon"
        >
          <Icon name="delete-trash" />
        </div>
      </div>
      <div class="sku-item-r">
        <div class="unit-price">
          <IntlCurrency :currency="currency.current.targetCurrency" />
          {{ unit }}
          <span class="suffix"> / Pcs</span>
        </div>
        <div class="total-price">
          <IntlCurrency :currency="currency.current.targetCurrency" />
          {{ price }}
        </div>
      </div>
    </div>
    <p
      class="sku-item-error"
      v-if="item.unavailableMsg"
    >
      <Icon
        class="sku-item-error__icon"
        name="icon-warning-outline"
      />
      {{ item.unavailableMsg }}
      <span
        class="sku-item-error__contact"
        @click="contactUs()"
      >
        Contact us
      </span>
    </p>

    <ExceedQuantityDialog
      v-if="showExceedQuantityDialog"
      @close="handleCloseExceedQuantityDialog"
      :quantity="minOrderQuantity"
    />
  </div>
</template>

<script setup>
import { Icon } from '@/components/Icon'
import { IntlCurrency } from '@/components/NewIntlCurrency'
import NumberInput from '@/components/NumberInput/index.vue'
import ExceedQuantityDialog from '@/components/ExceedQuantityDialog/index.vue'
import { useCurrencyStore } from '@/pinia/currency'
import { useCartSku } from '@/views/Carts/hooks/useCartSku'
import CartsSelect from '@/views/Carts/components/CartsSelect.vue'
import { useContactApp } from '@/hooks/useContactApp'
import { Tooltip } from '@/components/Tooltip'
import { buriedPointUtil } from '@/utils/utils'
import { Image } from '@/components/Image'

const props = defineProps({
  item: {
    type: Object,
    default: () => ({})
  },
  allProcessing: {
    type: Boolean,
    default: false
  }
})

const { contactUs } = useContactApp()

const { unit, price, handleCheck, handleDeleteClick } = useCartSku(computed(() => props.item))

const minOrderQuantity = ref(0)

const showExceedQuantityDialog = ref(false)

const currency = useCurrencyStore()

const handleCheckClick = () => {
  buriedPointUtil({ eventId: 'PCL202', type: 'SKU' })
  handleCheck()
}
</script>

<style lang='less' scoped>
.sku-item {
  position: relative;
  margin: 0 -8px;
  padding: 16px 8px;

  &--disabled .sku-item__content {
    filter: grayscale(1) opacity(0.5);
  }

  &::after {
    content: '';
    position: absolute;
    left: 8px;
    bottom: 0;
    right: 0;
    height: 1px;
    background-color: #ebf1f5;
  }
  &:last-child::after {
    display: none;
  }

  &.processing {
    background-color: #FFFCF7;

    .sku-item-r .total-price {
      color: #6E717A;
    }
  }

  &__content {
    display: flex;
    align-items: center;
  }

  .sku-item-l {
    display: flex;
    align-items: center;
    .check-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      font-size: 16px;
      cursor: pointer;
    }
    .image {
      width: 40px;
      height: 40px;
      border-radius: 4px;
      border: 1px solid #EBF1F5;
      margin-left: 10px;
    }
    .skuSpec {
      width: 166px;
      height: 32px;
      line-height: 32px;
      border-radius: 4px;
      background-color: #f8f8f8;
      margin-left: 12px;
      padding: 0 8px;
      color: #6e717a;
      font-size: 12px;
      font-family: mon-500;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      @media (min-width: 1280px) {
        width: 216px;
      }
    }
    .number-input-wrapper {
      display: flex;
      align-items: center;
      margin-left: 16px;
      width: 96px;
      height: 32px;

      @media (min-width: 1280px) {
        margin-left: 24px;
        width: 108px;
      }
    }
    .delete-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      margin-left: 16px;
      font-size: 24px;
      color: #6E717A;

      &:hover {
        cursor: pointer;
      }
    }
  }
  .sku-item-r {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-left: auto;

    @media (min-width: 1280px) {
      margin-left: 24px;
      flex: 1;
      flex-direction: row;
      justify-content: space-between;
    }

    .unit-price {
      color: #6e717a;
      font-size: 12px;
      font-family: mon-600;

      @media (min-width: 1280px) {
        margin-left: 24px;
      }

      .suffix {
        font-size: 10px;
      }
    }

    .total-price {
      color: #232323;
      font-size: 12px;
      font-family: mon-700;
    }
  }

  &-error {
    margin-top: 8px;
    padding-left: 26px;
    font-family: mon-500;
    font-size: 12px;
    line-height: 16px;
    color: #7A0505;

    &__icon {
      font-size: 14px;
    }

    &__contact {
      margin-left: 6px;
      text-decoration: underline;
      color: #232323;
      cursor: pointer;

      &:hover {
        color: #004F9A;
      }
    }
  }

  .processing {
    position: absolute;
    top: 0;
    right: 0;
    width: 67px;
    height: 18px;
    line-height: 18px;
    text-align: center;
    border-radius: 10px;
    background-color: #ffa033;
    color: white;
    font-size: 10px;
    font-family: mon-600;
  }
}
</style>
